import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import {
  Button,
  Select,
  Spinner,
  StatusBadge,
} from '../../components/design-system';
import { AdminPage } from './AdminPage';
import { UserDetailModal } from './UserDetailModal';
import type { MatchStatus } from '../../types';

interface MatchRow {
  id: string;
  traveler_id: string;
  host_id: string;
  status: MatchStatus;
  rank: number | null;
  match_note: string | null;
  needs_host: boolean;
  created_at: string;
  traveler: { full_name: string | null } | null;
  host: { full_name: string | null } | null;
}

interface HostOption {
  profile_id: string;
  full_name: string | null;
  city: string | null;
}

export function AdminHostMatching() {
  const { user } = useAuth();
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [detailUser, setDetailUser] = useState<string | null>(null);
  const [hosts, setHosts] = useState<HostOption[]>([]);
  const [assignMatch, setAssignMatch] = useState<MatchRow | null>(null);
  const [assignHostId, setAssignHostId] = useState('');
  const [assigning, setAssigning] = useState(false);
  async function loadMatches() {
    setDataLoading(true);
    const { data } = await supabase
      .from('matches')
      .select('*, traveler:profiles!matches_traveler_id_fkey(full_name), host:profiles!matches_host_id_fkey(full_name)')
      .order('created_at', { ascending: false });
    // Escalated (needs_host) rows float to the top so they're actioned first.
    const rows = (data as unknown as MatchRow[]) ?? [];
    rows.sort((a, b) => Number(b.needs_host) - Number(a.needs_host));
    setMatches(rows);
    setDataLoading(false);
  }

  useEffect(() => {
    if (!user) return;
    void loadMatches();
  }, [user]);

  // Active hosts for manual assignment.
  useEffect(() => {
    if (!user) return;
    void (async () => {
      const { data: hp } = await supabase
        .from('host_profiles')
        .select('profile_id, city')
        .eq('active', true);
      const rows = (hp as { profile_id: string; city: string | null }[]) ?? [];
      if (rows.length === 0) { setHosts([]); return; }
      const ids = rows.map((r) => r.profile_id);
      const { data: profs } = await supabase.from('profiles').select('id, full_name').in('id', ids);
      const nameMap = new Map(((profs as { id: string; full_name: string | null }[]) ?? []).map((p) => [p.id, p.full_name]));
      setHosts(rows.map((r) => ({ profile_id: r.profile_id, full_name: nameMap.get(r.profile_id) ?? null, city: r.city })));
    })();
  }, [user]);

  function openAssign(m: MatchRow) {
    setAssignMatch(m);
    setAssignHostId('');
  }

  async function confirmAssign() {
    if (!assignMatch || !assignHostId || assigning) return;
    setAssigning(true);
    const { error } = await supabase.rpc('admin_assign_host', {
      p_match_id: assignMatch.id,
      p_host_id: assignHostId,
    });
    setAssigning(false);
    if (error) return;
    setAssignMatch(null);
    void loadMatches();
  }

  return (
    <AdminPage title="Host Matching" description="Traveler-to-host matches and their status.">
      {dataLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-app-border bg-app-card shadow-sm">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-app-border bg-app-bg">
                <th className="text-left px-4 py-3 font-medium text-app-muted">Traveler</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted">Host</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted">Status</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted">Rank</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted">Created</th>
                <th className="text-left px-4 py-3 font-medium text-app-muted"></th>
              </tr>
            </thead>
            <tbody>
              {matches.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-app-muted text-xs">No matches.</td></tr>
              )}
              {matches.map((m) => (
                <tr key={m.id} className="border-b border-app-border last:border-0 hover:bg-app-bg/50">
                  <td className="px-4 py-3 text-xs">
                    <button onClick={() => setDetailUser(m.traveler_id)} className="text-app-fg hover:text-gold transition-colors underline underline-offset-2 decoration-app-border hover:decoration-gold text-left">
                      {m.traveler?.full_name ?? <span className="font-mono">{m.traveler_id.slice(0, 8)}…</span>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {m.needs_host ? (
                      <span className="text-app-muted italic">Unassigned</span>
                    ) : (
                      <button onClick={() => setDetailUser(m.host_id)} className="text-app-fg hover:text-gold transition-colors underline underline-offset-2 decoration-app-border hover:decoration-gold text-left">
                        {m.host?.full_name ?? <span className="font-mono">{m.host_id.slice(0, 8)}…</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {m.needs_host ? (
                      <span className="inline-flex items-center gap-1.5 rounded-pill border border-crimson/30 bg-crimson/10 text-crimson px-2.5 py-[3px] text-[11px] font-medium">
                        <i className="ti ti-alert-triangle text-[11px]" />Needs host
                      </span>
                    ) : (
                      <StatusBadge status={m.status} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-app-muted text-xs">{m.rank ?? '—'}</td>
                  <td className="px-4 py-3 text-app-muted text-xs">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {m.needs_host && (
                      <Button variant="secondary" onClick={() => openAssign(m)}>Assign host</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailUser && (
        <UserDetailModal userId={detailUser} onClose={() => setDetailUser(null)} />
      )}

      {assignMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => !assigning && setAssignMatch(null)}>
          <div className="w-full max-w-sm bg-app-card border border-app-border rounded-2xl p-5 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <h2 className="text-base font-semibold text-app-fg">Assign a host</h2>
              <p className="text-xs text-app-muted mt-1">
                {assignMatch.traveler?.full_name ?? 'This traveler'} paid but both proposed hosts passed. Pick a host to take the trip.
              </p>
            </div>
            <Select
              options={hosts.map((h) => ({ value: h.profile_id, label: h.city ? `${h.full_name ?? 'Host'} · ${h.city}` : (h.full_name ?? 'Host') }))}
              placeholder="Choose a host…"
              value={assignHostId}
              onChange={(e) => setAssignHostId(e.target.value)}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setAssignMatch(null)} disabled={assigning}>Cancel</Button>
              <Button onClick={() => void confirmAssign()} disabled={!assignHostId || assigning}>
                {assigning ? <Spinner size="sm" /> : 'Assign & notify'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

export default AdminHostMatching;
